import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { ITrainingEmployee } from '../../../../common/data/dtos/training-employee';
import TrainingEmployeeStatusEnum from '../../../../common/data/enums/enumTrainingEmployeeStatus';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardFooterRight, CardTabItem } from '../../../../components/bootstrap/Card';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import Spinner from '../../../../components/bootstrap/Spinner';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Input from '../../../../components/bootstrap/forms/Input';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import trainingEmployeeService from '../../../../services/training-employee-service';

type TrainingEmployeeFormValues = {
    id?: string;
    employeeId?: string;
    trainingId?: string;
    doneDate?: string;
    situation?: number;
};

interface Props {
    isModalOpened: boolean;
    selectedTrainingEmployee: ITrainingEmployee | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

const TrainingCompletedModal = ({ isModalOpened, selectedTrainingEmployee, onModalClose, onAfterSubmit }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedTrainingStatus, setSelectedTrainingStatus] = useState<string>('');

    const formik = useFormik({
        initialValues: {
            id: '',
            employeeId: '',
            trainingId: '',
            doneDate: '',
            situation: 0,
        },

        validate: (values) => {
            const errors: {
                id?: string;
                employeeId?: string;
                trainingId?: string;
                doneDate?: Date;
                situation?: number;
            } = {};

            if (!values.doneDate) errors.id = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    useEffect(() => {
        if (selectedTrainingEmployee) {
            setSelectedTrainingStatus('');
        }
    }, [isModalOpened]);

    const onClose = () => {
        onModalClose();
    };

    const onFormSubmit = async (values: TrainingEmployeeFormValues) => {
        if (selectedTrainingEmployee) {
            setIsSubmitting(true);

            const response = await trainingEmployeeService.updateTrainingEmployee({
                id: selectedTrainingEmployee?.id,
                doneDate: values.doneDate ? dayjs(values.doneDate).toDate() : undefined,
                situation: TrainingEmployeeStatusEnum.done,
                employeeId: selectedTrainingEmployee?.employeeId ?? '',
                trainingId: selectedTrainingEmployee?.trainingId ?? '',
                rowVersion: selectedTrainingEmployee.rowVersion,
                status: selectedTrainingStatus !== '' ? Number(selectedTrainingStatus) : selectedTrainingEmployee.status,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
            onAfterSubmit();
        }
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size="lg" isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id="return-work-resource-modal">Conclusão de treinamento</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor="primary">
                    <CardTabItem id="tab-item-1" title="Conclusão" icon="Done">
                        <div className="row g-4">
                            <div className="col-lg-12 col-md-12 col-sm-12">
                                <FormGroup id="doneDate" label="Data de conclusão" isFloating>
                                    <Input type="date" onChange={formik.handleChange} onBlur={formik.handleBlur} value={formik.values.doneDate} />
                                </FormGroup>
                            </div>
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className="bg-transparent">
                <CardFooterRight>
                    <Button color="success" className="w-100" onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
};

export default TrainingCompletedModal;
